import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, setupAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowLeft, Shield, ShieldCheck, ArrowRight, Phone, FileText, ClipboardCheck, DollarSign, FileCheck, BarChart3 } from 'lucide-react';

const LOGO_URL = process.env.REACT_APP_LOGO_URL || '/sobs-logo.png';

// Shared auth shell - mirrors imis.sobs.gov.so/admin Staff Portal layout
const PORTAL_FEATURES = [
  { icon: FileText, title: 'Cargo Manifests', desc: 'Airline manifest intake, AI extraction and AWB tracking' },
  { icon: ClipboardCheck, title: 'Inspections', desc: 'Sector checklists, document verification and outcomes' },
  { icon: ShieldCheck, title: 'Review & Approval', desc: 'Admin clearance review before gate pass issuance' },
  { icon: DollarSign, title: 'Finance', desc: 'Fee assessment, payment status and finance audit' },
  { icon: FileCheck, title: 'Gate Passes', desc: 'QR-verified SoBS gate passes for cleared cargo' },
  { icon: BarChart3, title: 'Reports', desc: 'Daily, medical, non-medical and revenue reporting' },
];

export const AuthShell = ({ children, title, subtitle, testId }) => (
  <div className="min-h-screen flex flex-col bg-[#F4F6F9]">
    {/* Top government strip */}
    <div className="bg-[#081C36] text-white/85 text-[11px] font-semibold tracking-[0.12em] uppercase" data-testid="top-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-8 flex items-center justify-between">
        <a href="mailto:info@sobs.gov.so" className="inline-flex items-center gap-1.5 hover:text-[#FFC107] transition-colors normal-case tracking-normal">
          <Mail className="h-3 w-3" /> info@sobs.gov.so
        </a>
        <span className="inline-flex items-center gap-2 font-bold">
          <Phone className="h-3 w-3" /> Hotline
          <span className="bg-[#FFC107] text-[#0B2545] px-2 py-0.5 rounded-sm tracking-widest">2022</span>
        </span>
      </div>
    </div>

    {/* White header */}
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[76px] flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <img src={LOGO_URL} alt="SoBS Logo" className="h-12 w-auto object-contain" />
          <div className="h-9 w-px bg-slate-200" />
          <div className="leading-tight min-w-0">
            <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[#B8860B]">SOBS-AAIA</span>
            <span className="block text-[16px] font-extrabold text-[#0B2545] tracking-tight truncate">Staff Portal</span>
          </div>
        </div>
      </div>
    </header>

    {/* Navy band */}
    <section className="login-bg relative text-white overflow-hidden" data-testid="auth-hero">
      <div className="brand-watermark-hero" aria-hidden="true" data-testid="brand-watermark" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-24 sm:pb-28">
        <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#FFC107]">Staff Portal</p>
        <h1 className="mt-2 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white">Sign in to your workspace</h1>
        <p className="mt-2 text-[13px] sm:text-[14px] text-white/70 max-w-[560px]">Authorised SoBS staff only. Every session is logged for audit purposes.</p>
      </div>
    </section>

    {/* Card + features */}
    <section className="flex-1 relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-16 sm:-mt-20 pb-12 grid grid-cols-1 md:grid-cols-[360px_1fr] gap-6 lg:gap-8 items-start">
        <div className="bg-white border-t-4 border-[#FFC107] shadow-2xl shadow-[#0B2545]/15 p-6" data-testid={testId}>
          <div className="flex items-center gap-2 mb-5">
            <ShieldCheck className="h-5 w-5 text-[#0B2545]" />
            <h2 className="text-[16px] font-bold text-[#0B2545]">{title}</h2>
          </div>
          {subtitle && <p className="-mt-3 mb-5 text-[12.5px] text-slate-500 leading-relaxed">{subtitle}</p>}
          {children}
          <hr className="my-5 border-slate-200" />
          <p className="text-[11.5px] leading-relaxed text-slate-500">
            Access is restricted to authorised SoBS staff. Forgot your password or locked out? Contact the system administrator or call the hotline <span className="font-bold text-[#0B2545]">2022</span>.
          </p>
        </div>

        <div className="hidden md:block md:pt-20">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 mt-1 mb-3">Managed in this portal</p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {PORTAL_FEATURES.map((f) => (
              <div key={f.title} className="bg-white border border-slate-200 p-4 flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#0B2545] text-white">
                  <f.icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-[13.5px] font-bold text-[#0B2545] leading-tight">{f.title}</p>
                  <p className="mt-1 text-[12px] text-slate-500 leading-snug">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <footer className="bg-[#081C36] text-white/70 py-5 px-6">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-xs">
        <span className="font-bold text-white">Somali Bureau of Standards · Aden Abdulle International Airport</span>
        <span className="uppercase tracking-[0.14em] text-[10px] font-semibold">© {new Date().getFullYear()} SoBS · Somalia's Quality Authority</span>
      </div>
    </footer>
  </div>
);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [pendingEmail, setPendingEmail] = useState('');
  const [mfaMethod, setMfaMethod] = useState('email');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showForceReset, setShowForceReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  const { login, verify2FA, completeForcedReset, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Initialize system on first load
    setupAPI.init().catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      // Finance users should go to Finance page, not Dashboard
      if (user.role === 'finance') {
        navigate('/admin/finance');
      } else {
        navigate('/admin/dashboard');
      }
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires2FA) {
        setPendingEmail(email);
        setMfaMethod(result.mfaMethod || 'email');
        setShow2FA(true);
        toast.success(result.mfaMethod === 'totp' ? 'Enter the code from your authenticator app' : 'Verification code sent to your email');
      } else if (result.passwordResetRequired) {
        setPendingEmail(result.email || email);
        setShowForceReset(true);
        toast.warning('For security reasons you must set a new password before continuing');
      } else {
        toast.success('Login successful');
        if (result.mfaEnrollmentRequired) {
          toast.warning('Multi-factor authentication is required for your role. Please enrol an authenticator app under Account Security.', { duration: 9000 });
        }
        if (result.user?.role === 'finance') {
          navigate('/admin/finance');
        } else {
          navigate('/admin/dashboard');
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(d => d.msg).join(', ')
        : error.response ? `Sign-in failed (error ${error.response.status}). Please try again or contact the System Administrator.`
        : (error.message || 'Unable to reach the server. Check your connection and try again.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      toast.error('Please enter the complete verification code');
      return;
    }

    setLoading(true);
    try {
      const result = await verify2FA(pendingEmail, otpCode);
      toast.success('Login successful');
      if (result?.user?.role === 'finance') {
        navigate('/admin/finance');
      } else {
        navigate('/admin/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
      setOtp(['', '', '', '', '', '']);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      await authAPI.resendOTP(pendingEmail);
      toast.success('New verification code sent');
    } catch (error) {
      toast.error('Failed to resend code');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.forgotPassword(resetEmail);
      toast.success('If the email exists, reset instructions will be sent');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleForceReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const userData = await completeForcedReset(pendingEmail, password, newPassword);
      toast.success('Password updated — welcome back');
      if (userData?.role === 'finance') {
        navigate('/admin/finance');
      } else {
        navigate('/admin/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const backLink = (onClick, testId) => (
    <button
      type="button"
      onClick={onClick}
      className="text-sm font-semibold text-slate-500 hover:text-[#FF5722] flex items-center justify-center gap-1 mx-auto transition-colors"
      data-testid={testId}
    >
      <ArrowLeft className="h-4 w-4" /> Back to login
    </button>
  );

  // Mandatory Password Reset Screen (forced security reset)
  if (showForceReset) {
    return (
      <AuthShell
        testId="force-reset-card"
        title="Security Password Update Required"
        subtitle="For security reasons, you must set a new password before accessing the system. Your account, records and permissions are unchanged."
      >
        <form onSubmit={handleForceReset} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pl-10 pr-10 h-11 bg-white"
                placeholder="Enter new password"
                required
                data-testid="force-reset-new-password"
              />
              <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0B2545]">
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">Confirm New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type={showNewPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10 h-11 bg-white"
                placeholder="Re-enter new password"
                required
                data-testid="force-reset-confirm-password"
              />
            </div>
          </div>
          <p className="text-xs text-slate-500">Minimum 8 characters. Must be different from your current password.</p>
          <Button type="submit" className="w-full h-11" disabled={loading} data-testid="force-reset-submit-btn">
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...</>
            ) : (
              <><Shield className="mr-2 h-4 w-4" /> Set New Password & Continue</>
            )}
          </Button>
          {backLink(() => { setShowForceReset(false); setNewPassword(''); setConfirmPassword(''); }, 'force-reset-back-btn')}
        </form>
      </AuthShell>
    );
  }

  // 2FA Verification Screen
  if (show2FA) {
    return (
      <AuthShell testId="two-factor-card" title="Two-Factor Authentication" subtitle={mfaMethod === 'totp' ? 'Enter the 6-digit code from your authenticator app, or a recovery code' : 'Enter the 6-digit code sent to your email'}>
        <form onSubmit={handleVerify2FA} className="space-y-6">
          <div className="flex justify-center gap-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                className="otp-input bg-white"
                data-testid={`otp-input-${index}`}
              />
            ))}
          </div>

          <Button type="submit" className="w-full h-11" disabled={loading} data-testid="verify-otp-btn">
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>
            ) : (
              <><Shield className="mr-2 h-4 w-4" /> Verify Code</>
            )}
          </Button>

          <div className="text-center space-y-2">
            {mfaMethod === 'totp' ? (
              <input
                type="text"
                placeholder="Or paste a 10-character recovery code"
                className="w-full h-10 border border-slate-300 px-3 text-sm bg-white"
                data-testid="recovery-code-input"
                onChange={(e) => {
                  const v = e.target.value.trim();
                  if (v.length === 10) {
                    verify2FA(pendingEmail, v)
                      .then(() => { toast.success('Login successful'); navigate('/admin/dashboard'); })
                      .catch((err) => toast.error(err.response?.data?.detail || 'Invalid recovery code'));
                  }
                }}
              />
            ) : (
            <button
              type="button"
              onClick={handleResendOTP}
              className="text-sm font-semibold text-[#0B2545] underline decoration-[#FFC107] decoration-2 underline-offset-[3px] hover:text-[#FF5722] transition-colors"
              data-testid="resend-otp-btn"
            >
              Resend verification code
            </button>
            )}
            <br />
            {backLink(() => { setShow2FA(false); setOtp(['', '', '', '', '', '']); }, 'otp-back-btn')}
          </div>
        </form>
      </AuthShell>
    );
  }

  // Forgot Password Screen
  if (showForgotPassword) {
    return (
      <AuthShell testId="forgot-password-card" title="Reset Password" subtitle="Enter your email to receive reset instructions">
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-email" className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="reset-email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="pl-10 h-11 bg-white"
                placeholder="Enter your email"
                required
                data-testid="reset-email-input"
              />
            </div>
          </div>

          <Button type="submit" className="w-full h-11" disabled={loading} data-testid="send-reset-btn">
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
            ) : (
              'Send Reset Link'
            )}
          </Button>

          {backLink(() => setShowForgotPassword(false), 'forgot-back-btn')}
        </form>
      </AuthShell>
    );
  }

  // Main Login Screen
  return (
    <AuthShell testId="login-card" title="Staff sign in">
      <form onSubmit={handleLogin} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">Email Address</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 h-11 bg-white"
              placeholder="Enter your email"
              required
              data-testid="email-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">Password</Label>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-xs font-semibold text-[#0B2545]/70 hover:text-[#FF5722] transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 pr-10 h-11 bg-white"
              placeholder="Enter your password"
              required
              data-testid="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0B2545]"
              data-testid="toggle-password-btn"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <Button type="submit" className="w-full h-11 group" disabled={loading} data-testid="login-btn">
          {loading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</>
          ) : (
            <>Sign In <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>
          )}
        </Button>
      </form>
    </AuthShell>
  );
};

export default Login;
